import React, { useState } from 'react';
import axios from 'axios';
import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import authConfig from '../../authConfig';
import { Form, Button, ProgressBar, Alert, Row, Col, Spinner } from 'react-bootstrap';

const VideoUploader = ({ fileId: propFileId, onUpload }) => {
    const [file, setFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);

    const { instance, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const account = useAccount(accounts[0] || {});

    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks

    const isMp4 = (f) => {
        if (!f) return false;
        const nameOk = f.name && f.name.toLowerCase().endsWith('.mp4');
        const typeOk = f.type === 'video/mp4';
        return typeOk || nameOk;
    };

    const handleFileChange = (e) => {
        setError(null);
        if (e.target.files.length > 0) {
            const selected = e.target.files[0];
            if (!isMp4(selected)) {
                setFile(null);
                setError('Only MP4 files are allowed. Please select a .mp4 video.');
                // reset the input so the same file can be re-selected if needed
                e.target.value = null;
                return;
            }
            setFile(selected);
        } else {
            setFile(null);
        }
    };

    const acquireToken = async () => {
        try {
            const response = await instance.acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account: account
            });
            return response?.accessToken ?? null;
        } catch (silentError) {
            try {
                await instance.acquireTokenRedirect({
                    scopes: authConfig.api.scopes,
                    account: account
                });
            } catch (redirectErr) {
                console.error('Token redirect failed', redirectErr);
            }
            return null;
        }
    };

    const uploadChunks = async () => {
        setError(null);

        if (!isAuthenticated) {
            try {
                await instance.loginRedirect();
            } catch (loginErr) {
                console.error('Login redirect failed', loginErr);
                setError('Login required to upload files.');
            }
            return;
        }

        if (!file) {
            setError('No file selected.');
            return;
        }

        // final safety check
        if (!isMp4(file)) {
            setError('Selected file is not an MP4.');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const token = await acquireToken();
            if (!token) {
                setError('Unable to acquire access token.');
                setIsUploading(false);
                return;
            }

            const axiosInstance = axios.create({
                headers: { Authorization: `Bearer ${token}` }
            });

            // Use fileId passed via props if provided; otherwise generate one
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

            for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                const start = chunkIndex * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                const formData = new FormData();
                formData.append('file', chunk);
                formData.append('fileName', file.name);
                formData.append('chunkIndex', chunkIndex);
                formData.append('totalChunks', totalChunks);
                formData.append('fileId', propFileId);

                await axiosInstance.post(authConfig.api.urls.getMemberTrainingUploadChunk, formData, {
                    onUploadProgress: (progressEvent) => {
                        const overallUploaded = start + (progressEvent.loaded || 0);
                        const progress = (overallUploaded / file.size) * 100;
                        setUploadProgress(Math.min(progress, 100));
                    }
                });
            }

            // Notify server to complete assembly
            await axiosInstance.post(authConfig.api.urls.getMemberTrainingCompleteUpload, { fileId: propFileId });

            setUploadProgress(100);

            // fire onUpload callback with filename after successful upload
            if (typeof onUpload === 'function') {
                try {
                    onUpload(file.name);
                } catch (cbErr) {
                    console.error('onUpload callback error', cbErr);
                }
            }
        } catch (err) {
            console.error('Upload failed:', err);
            setError(err?.message || 'Upload failed');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Form>
            <Form.Group controlId="videoFile">
                <Form.Label>Select video mp4 file</Form.Label>
                {/* restrict file picker to MP4; also enforce on-change validation */}
                <Form.Control
                    type="file"
                    accept=".mp4,video/mp4"
                    onChange={handleFileChange}
                    disabled={isUploading}
                />
                {file && (
                    <Form.Text className="text-muted">
                        Selected: {file.name} ({Math.round(file.size / 1024 / 1024)} MB)
                    </Form.Text>
                )}
            </Form.Group>

            <Row className="mt-3">
                <Col xs="auto">
                    <Button
                        variant="primary"
                        onClick={uploadChunks}
                        disabled={!file || isUploading}
                    >
                        {isUploading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{' '}
                                Uploading... {Math.round(uploadProgress)}%
                            </>
                        ) : (
                            'Upload'
                        )}
                    </Button>
                </Col>
                <Col className="align-self-center">
                    <ProgressBar now={uploadProgress} label={uploadProgress ? `${Math.round(uploadProgress)}%` : ''} />
                </Col>
            </Row>

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
        </Form>
    );
};

export default VideoUploader;
